"use client"
import { useCallback, useEffect, useRef, useState } from "react"

// Returns favourites backed by DB when logged in, localStorage when not.
export function useFavourites() {
  const [favs, setFavs] = useState<string[]>([])
  const loggedIn = useRef(false)

  useEffect(() => {
    fetch("/api/favourites")
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          // Authenticated, merge any local favs then use DB
          loggedIn.current = true
          const local: string[] = JSON.parse(localStorage.getItem("ze_favs") ?? "[]")
          const merged = Array.from(new Set([...d.data, ...local]))
          setFavs(merged)
          // Push any locally-saved favs that aren't in DB yet
          for (const id of local) {
            if (!d.data.includes(id)) {
              fetch("/api/favourites", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId: id }) }).catch(() => {})
            }
          }
          localStorage.removeItem("ze_favs")
        } else {
          // Unauthenticated, use localStorage
          loggedIn.current = false
          setFavs(JSON.parse(localStorage.getItem("ze_favs") ?? "[]"))
        }
      })
      .catch(() => {
        setFavs(JSON.parse(localStorage.getItem("ze_favs") ?? "[]"))
      })
  }, [])

  const toggle = useCallback((listingId: string, e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    setFavs(prev => {
      const isSaved = prev.includes(listingId)
      const next = isSaved ? prev.filter(f => f !== listingId) : [...prev, listingId]
      if (loggedIn.current) {
        if (isSaved) {
          fetch("/api/favourites", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId }) }).catch(() => {})
        } else {
          fetch("/api/favourites", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId }) }).catch(() => {})
        }
      } else {
        localStorage.setItem("ze_favs", JSON.stringify(next))
      }
      return next
    })
  }, [])

  const remove = useCallback((listingId: string) => {
    setFavs(prev => {
      const next = prev.filter(f => f !== listingId)
      if (loggedIn.current) {
        fetch("/api/favourites", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId }) }).catch(() => {})
      } else {
        localStorage.setItem("ze_favs", JSON.stringify(next))
      }
      return next
    })
  }, [])

  return { favs, toggle, remove, isLoggedIn: () => loggedIn.current }
}
